import { useSyncExternalStore } from "react";
import { isAppNavigation } from "./routes";

interface InstallPrompt extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
interface PwaState {
    offline: boolean;
    installed: boolean;
    installPrompt: InstallPrompt | null;
}
const display = window.matchMedia("(display-mode: standalone)");
const installed = () => display.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
let state: PwaState = { offline: !navigator.onLine, installed: installed(), installPrompt: null };
const listeners = new Set<() => void>();
let registration: ServiceWorkerRegistration | undefined;
let refreshing = false;
let reloading = false;
function set(values: Partial<PwaState>) {
    state = { ...state, ...values };
    listeners.forEach((listener) => listener());
}
export function usePwa() {
    return useSyncExternalStore((listener) => {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    }, () => state);
}

function reloadApp() {
    if (reloading || !isAppNavigation(new URL(window.location.href))) return;
    reloading = true;
    window.location.reload();
}

/** Compare actual builds: first installation and same-build controller changes
 * must not refresh the page. A newer active build refreshes every open app tab. */
async function followWorkerVersion(): Promise<boolean> {
    const controller = navigator.serviceWorker?.controller;
    if (!controller || !isAppNavigation(new URL(window.location.href))) return false;
    const version = await new Promise<string | null>((resolve) => {
        const channel = new MessageChannel();
        const timer = window.setTimeout(() => { channel.port1.close(); resolve(null); }, 5000);
        channel.port1.onmessage = (event) => {
            window.clearTimeout(timer);
            channel.port1.close();
            resolve(typeof event.data?.version === "string" ? event.data.version : null);
        };
        try { controller.postMessage({ type: "PM_WORKER_VERSION" }, [channel.port2]); }
        catch {
            window.clearTimeout(timer);
            channel.port1.close();
            resolve(null);
        }
    });
    if (version && version !== __PM_BUILD_ID__) {
        reloadApp();
        return true;
    }
    return false;
}

/** Server build changes and missing chunks also recover silently. Let an
 * installing worker finish precaching before reloading onto its new shell. */
export async function requestAppUpdate() {
    if (refreshing || reloading || !navigator.onLine || !isAppNavigation(new URL(window.location.href))) return;
    refreshing = true;
    try {
        if (!registration && "serviceWorker" in navigator) registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
            try { await registration.update(); } catch { return; }
            if (registration.installing || registration.waiting) return;
            if (await followWorkerVersion()) return;
        }
        reloadApp();
    } finally { refreshing = false; }
}

export async function installApp() {
    const prompt = state.installPrompt;
    if (!prompt) return;
    set({ installPrompt: null });
    try { await prompt.prompt(); await prompt.userChoice; }
    catch { /* The browser can withdraw installation eligibility. Its menu still works. */ }
}

/** Production only: Vite's development worker must not own a developer's origin. */
export function startPwa() {
    window.addEventListener("online", () => { set({ offline: false }); void checkUpdate(); });
    window.addEventListener("offline", () => set({ offline: true }));
    window.addEventListener("beforeinstallprompt", (event) => {
        event.preventDefault();
        set({ installPrompt: event as InstallPrompt });
    });
    window.addEventListener("appinstalled", () => set({ installed: true, installPrompt: null }));
    display.addEventListener("change", () => set({ installed: installed() }));
    if (!import.meta.env.PROD || !window.isSecureContext || !("serviceWorker" in navigator)) return;

    navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "PM_VERSION") event.ports[0]?.postMessage({ version: __PM_BUILD_ID__ });
    });
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        navigator.serviceWorker.controller?.postMessage({ type: "PM_CLIENT_READY" });
        void followWorkerVersion();
    });
    const register = async () => {
        try {
            registration = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
            navigator.serviceWorker.controller?.postMessage({ type: "PM_CLIENT_READY" });
            await followWorkerVersion();
            if (!reloading) await checkUpdate();
        } catch { /* Online use continues if installation/storage is unavailable. */ }
    };
    if (document.readyState === "complete") void register();
    else window.addEventListener("load", () => void register(), { once: true });
    let lastCheck = Date.now();
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        void followWorkerVersion();
        if (Date.now() - lastCheck > 60_000) {
            lastCheck = Date.now();
            navigator.serviceWorker.controller?.postMessage({ type: "PM_CLIENT_READY" });
            void checkUpdate();
        }
    });
    window.setInterval(() => { if (document.visibilityState === "visible") void checkUpdate(); }, 60 * 60 * 1000);
}
async function checkUpdate() {
    if (navigator.onLine) await registration?.update().catch(() => {});
}
